import { API_BASE_URL } from 'lib/config/apiEndpoints';

/** India 6-digit pincode; same shape as Blood Vision checkout. */
export const IN_PINCODE_RE = /^[1-9][0-9]{5}$/;

export type SddDeliveryMode = 'same_day' | 'two_hour' | 'next_day';

export type SddServiceabilityData = {
  eligible: boolean;
  pincode: string;
  city?: string;
  mode?: SddDeliveryMode;
  cutoff_ist?: string;
  eta_date?: string;
  /** Latest arrival instant, e.g. 2026-09-02T15:00:00+05:30. Display-only. */
  delivery_by?: string;
  /** Order-by instant, e.g. 2026-09-03T19:00:00+05:30. Timer deadline. */
  order_by?: string;
  title: string;
  subtitle?: string;
  advisory?: string;
};

type SddServiceabilityOk = {
  status: 'ok';
  data: SddServiceabilityData;
};

type SddServiceabilityErr = {
  status: 'error';
  error?: { code?: string | number; message?: string };
};

export type SddServiceabilityFailure = {
  ok: false;
  code: string;
  message: string;
};

export type SddServiceabilityResult =
  | { ok: true; data: SddServiceabilityData }
  | SddServiceabilityFailure;

export const isDeliveryMode = (value: unknown): value is SddDeliveryMode =>
  value === 'same_day' || value === 'two_hour' || value === 'next_day';

const asTrimmed = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed || undefined;
};

const parseData = (raw: unknown): SddServiceabilityData | null => {
  if (!raw || typeof raw !== 'object') return null;
  const d = raw as Record<string, unknown>;
  const title = asTrimmed(d.title);
  const pincode = asTrimmed(d.pincode);
  if (!pincode) return null;
  const eligible = d.eligible === true;
  // Eligible results must carry copy; ineligible may omit title until product lands it.
  if (eligible && !title) return null;
  return {
    eligible,
    pincode,
    title: title ?? '',
    city: asTrimmed(d.city),
    mode: isDeliveryMode(d.mode) ? d.mode : undefined,
    cutoff_ist: asTrimmed(d.cutoff_ist),
    eta_date: asTrimmed(d.eta_date),
    delivery_by: asTrimmed(d.delivery_by),
    order_by: asTrimmed(d.order_by),
    subtitle: asTrimmed(d.subtitle),
    advisory: asTrimmed(d.advisory),
  };
};

export async function fetchSddServiceability(
  pincode: string,
  signal?: AbortSignal,
): Promise<SddServiceabilityResult> {
  const url = `${API_BASE_URL}/api/web_v1/ring/sdd_serviceability?pincode=${encodeURIComponent(
    pincode,
  )}`;
  const response = await fetch(url, { signal });
  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    body = null;
  }

  if (response.status === 400) {
    const err = (body ?? {}) as SddServiceabilityErr;
    const rawCode = err.error?.code;
    return {
      ok: false,
      code:
        rawCode === 400 || rawCode === '400' || rawCode === 'invalid_pincode'
          ? 'invalid_pincode'
          : (asTrimmed(rawCode) ?? 'invalid_pincode'),
      message: asTrimmed(err.error?.message) ?? '',
    };
  }

  if (!response.ok) {
    return { ok: false, code: 'http_error', message: '' };
  }

  const parsed = body as SddServiceabilityOk | null;
  if (parsed?.status !== 'ok') {
    return { ok: false, code: 'bad_response', message: '' };
  }
  const data = parseData(parsed.data);
  if (!data) {
    return { ok: false, code: 'bad_response', message: '' };
  }
  return { ok: true, data };
}
